import { TInputData } from '@libs/types';
import { TValidationState } from '../types/data';

interface DateValidationResult {
    isValid: boolean;
    errorMessage?: string;
}

export function validateDate(
    dateString: string,
    mask = 'dd/mm/yyyy',
    mandatory?: TInputData['mandatory'],
    regex?: string,
    defaultErrorMessage?: string,
    constraints?: TValidationState[],
): DateValidationResult {
    if (!dateString) {
        if (!mandatory) {
            return { isValid: true };
        } else {
            return { isValid: false, errorMessage: defaultErrorMessage };
        }
    }

    const parseResult = stringToDate(dateString, mask);
    const { day, month, year } = parseResult;

    const inputDate = new Date(Date.UTC(year, month, day));

    // Validate that the date is actually valid (handles invalid dates like 31/02/2023)
    if (inputDate.getUTCFullYear() !== year || inputDate.getUTCMonth() !== month || inputDate.getUTCDate() !== day) {
        return {
            isValid: false,
            errorMessage: defaultErrorMessage,
        };
    }

    const currentDate = new Date();
    currentDate.setUTCHours(0, 0, 0, 0); // Reset time for accurate date comparison
    inputDate.setUTCHours(0, 0, 0, 0);

    if (Array.isArray(constraints) && constraints.length > 0) {
        // Check each constraint
        for (const constraint of constraints) {
            const validationResult = validateConstraint(inputDate, currentDate, constraint, defaultErrorMessage);
            if (!validationResult.isValid) {
                return validationResult;
            }
        }
    }
    let isValid = true;
    if (regex) {
        const regexPattern = new RegExp(regex);
        isValid = regexPattern.test(dateString);
    } else if (mandatory) {
        isValid = !!dateString && /\d/.test(dateString);
    }

    return {
        isValid,
        errorMessage: isValid ? undefined : defaultErrorMessage,
    };
}

export function stringToDate(dateString: string, mask: string) {
    // Helper function to find a token's index, checking for an uppercase and lowercase variant.
    const findIndex = (upper: string, lower: string): number => {
        let index = mask.indexOf(upper);
        if (index === -1) {
            index = mask.indexOf(lower);
        }
        return index;
    };

    // Find index for case-insensitive tokens
    const yearIndex = findIndex('YYYY', 'yyyy');
    const monthIndex = findIndex('MM', 'mm');
    const dayIndex = findIndex('DD', 'dd');

    // Extract parts using default values if a component is not in the mask
    const year = yearIndex > -1 ? parseInt(dateString.substring(yearIndex, yearIndex + 4), 10) : 1970;
    // Note: The month for the Date constructor is 0-indexed (0-11)
    const month = monthIndex > -1 ? parseInt(dateString.substring(monthIndex, monthIndex + 2), 10) - 1 : 0;
    const day = dayIndex > -1 ? parseInt(dateString.substring(dayIndex, dayIndex + 2), 10) : 1;

    return { day, month: month, year, isValid: true };
}

export function validateConstraint(inputDate: Date, currentDate: Date, constraint: TValidationState, defaultErrorMessage?: string): DateValidationResult {
    const { validationState, years = 0, months = 0, days = 0, errorMessage } = constraint;

    const currentUTCFullYear = currentDate.getUTCFullYear();
    const currentUTCMonth = currentDate.getUTCMonth();
    const currentUTCDate = currentDate.getUTCDate();

    switch (validationState) {
        case 'isFuture': {
            const futureDate = new Date(Date.UTC(currentUTCFullYear + years, currentUTCMonth + months, currentUTCDate + days));
            if (inputDate > futureDate) {
                return {
                    isValid: false,
                    errorMessage: errorMessage || defaultErrorMessage,
                };
            }
            break;
        }
        case 'isBefore': {
            const beforeDate = new Date(Date.UTC(currentUTCFullYear - years, currentUTCMonth - months, currentUTCDate - days));
            if (beforeDate < inputDate) {
                return {
                    isValid: false,
                    errorMessage: errorMessage || defaultErrorMessage,
                };
            }
            break;
        }
        case 'isAbove': {
            const aboveDate = new Date(Date.UTC(currentUTCFullYear - years, currentUTCMonth - months, currentUTCDate - days));
            if (inputDate <= aboveDate) {
                return {
                    isValid: false,
                    errorMessage: errorMessage || defaultErrorMessage,
                };
            }
            break;
        }
        default:
            return {
                isValid: true,
                errorMessage: defaultErrorMessage,
            };
    }

    return { isValid: true };
}
